/**
 * User Journey Tracker
 * Tracks user events and sends them to the backend (fire and forget)
 */

// Event enum constants - these are sent to the backend and converted to display names
export const EVENT_ENUMS = {
  // First event when a new user_journey_session_id is minted (UTM, referrer, landing path)
  JOURNEY_SESSION_ATTRIBUTION: 'JOURNEY_SESSION_ATTRIBUTION',
  /** 2-letter ISO country from /api/visitor-country (once per journey session). */
  VISITOR_COUNTRY: 'VISITOR_COUNTRY',

  // Page visit events
  HOME_PAGE_VISIT: 'HOME_PAGE_VISIT',
  FEATURES_PAGE_VISIT: 'FEATURES_PAGE_VISIT',
  BLOG_PAGE_VISIT: 'BLOG_PAGE_VISIT',
  ALTERNATIVES_PAGE_VISIT: 'ALTERNATIVES_PAGE_VISIT',
  PRICING_PAGE_VISIT: 'PRICING_PAGE_VISIT',
  DASHBOARD_PAGE_VISIT: 'DASHBOARD_PAGE_VISIT',
  RESULT_PAGE_VISIT: 'RESULT_PAGE_VISIT',
  TERMS_PAGE_VISIT: 'TERMS_PAGE_VISIT',
  POLICY_PAGE_VISIT: 'POLICY_PAGE_VISIT',
  REFUND_PAGE_VISIT: 'REFUND_PAGE_VISIT',
  
  // Authentication events
  LOGGED_IN: 'LOGGED_IN',
  LOGGED_OUT: 'LOGGED_OUT',
  SIGN_IN_CLICKED: 'SIGN_IN_CLICKED',
  LOGIN_FAILED: 'LOGIN_FAILED',
  TERMS_OPENED: 'TERMS_OPENED',
  POLICY_OPENED: 'POLICY_OPENED',
  REFUND_POLICY_OPENED: 'REFUND_POLICY_OPENED',
  
  // Video events
  VIDEO_SELECTED_TO_UPLOAD: 'VIDEO_SELECTED_TO_UPLOAD',
  VIDEO_URL_ADDED: 'VIDEO_URL_ADDED',
  VIDEO_TOO_LONG: 'VIDEO_TOO_LONG',
  VIDEO_TOO_LARGE: 'VIDEO_TOO_LARGE',
  UPLOAD_ANOTHER_VIDEO_CLICKED: 'UPLOAD_ANOTHER_VIDEO_CLICKED',
  CLIP_PREVIEW_PLAYED: 'CLIP_PREVIEW_PLAYED',

  // Phone number events
  CLICKED_PHONE_NO_DURING_PROCESSING: 'CLICKED_PHONE_NO_DURING_PROCESSING',
  PROVIDED_PHONE_NO_DURING_PROCESSING: 'PROVIDED_PHONE_NO_DURING_PROCESSING',
  CLICKED_PHONE_NO_DURING_DOWNLOAD: 'CLICKED_PHONE_NO_DURING_DOWNLOAD',
  PROVIDED_PHONE_NO_DURING_DOWNLOAD: 'PROVIDED_PHONE_NO_DURING_DOWNLOAD',

  // Pricing events
  PRICING_REGION_SHOWN: 'PRICING_REGION_SHOWN',
  SUBSCRIBE_NOW_CLICKED: 'SUBSCRIBE_NOW_CLICKED',
  /** Shown after Razorpay redirect to /?session=success (Creators Plan success overlay). */
  CHECKOUT_SUCCESS_PAGE_VISIT: 'CHECKOUT_SUCCESS_PAGE_VISIT',
  /** Premium user tapped pricing CTA to request cancellation via WhatsApp (prefilled message). */
  CANCEL_SUBSCRIPTION_WHATSAPP_CLICKED: 'CANCEL_SUBSCRIPTION_WHATSAPP_CLICKED',
  COUPON_CODE_APPLIED: 'COUPON_CODE_APPLIED',
  REFERRAL_INVITE_CLICKED: 'REFERRAL_INVITE_CLICKED',
  REFERRAL_CODE_COPIED: 'REFERRAL_CODE_COPIED',
  PAYMENT_ABANDONMENT_SURVEY_VISIT: 'PAYMENT_ABANDONMENT_SURVEY_VISIT',
  PAYMENT_ABANDONMENT_SURVEY_CLOSED: 'PAYMENT_ABANDONMENT_SURVEY_CLOSED',
  PAYMENT_ABANDONMENT_SURVEY_CONTINUE_CLICKED: 'PAYMENT_ABANDONMENT_SURVEY_CONTINUE_CLICKED',
  /** User expanded a pricing FAQ item (open only; stored as "FAQ clicked"; includes faq_question). */
  PRICING_FAQ_ITEM_OPENED: 'PRICING_FAQ_ITEM_OPENED',

  // Processing events
  YOUTUBE_STARTED_DURING_PROCESSING: 'YOUTUBE_STARTED_DURING_PROCESSING',
  YOUTUBE_PAUSED_DURING_PROCESSING: 'YOUTUBE_PAUSED_DURING_PROCESSING',

  // Streak reminders / push
  STREAK_REMINDERS_ENABLED: 'STREAK_REMINDERS_ENABLED',
  STREAK_REMINDERS_DENIED: 'STREAK_REMINDERS_DENIED',
  PUSH_NOTIFICATION_OPENED: 'PUSH_NOTIFICATION_OPENED',
  STREAK_OVERLAY_SHOWN: 'STREAK_OVERLAY_SHOWN',

  // Social publish / schedule
  ONE_TAP_PUBLISH_CLICKED: 'ONE_TAP_PUBLISH_CLICKED',
  PAST_PROJECT_CLICKED: 'PAST_PROJECT_CLICKED',
  PUBLISH_TO_SOCIAL_CLICKED: 'PUBLISH_TO_SOCIAL_CLICKED',
  CONNECT_ACCOUNT_CLICKED: 'CONNECT_ACCOUNT_CLICKED',
  ONE_TAP_SCHEDULE_CLICKED: 'ONE_TAP_SCHEDULE_CLICKED',
  SCHEDULE_A_CLIP_CLICKED: 'SCHEDULE_A_CLIP_CLICKED',
  SCHEDULE_CLIP_DIALOG_SUBMITTED: 'SCHEDULE_CLIP_DIALOG_SUBMITTED',
  ONE_TAP_SCHEDULE_PREFERENCES_SAVED: 'ONE_TAP_SCHEDULE_PREFERENCES_SAVED',

  // Daily content
  DAILY_CONTENT_IDEA_CLICKED: 'DAILY_CONTENT_IDEA_CLICKED',
  DAILY_CONTENT_GENERATE_CLICKED: 'DAILY_CONTENT_GENERATE_CLICKED',
};

// Button enum constants
export const BUTTON_ENUMS = {
  UPLOAD_BUTTON: 'UPLOAD_BUTTON',
  GENERATE_SHORTS_BUTTON: 'GENERATE_SHORTS_BUTTON',
  SIGNUP_BUTTON: 'SIGNUP_BUTTON',
  COUPON_CODE_APPLIED: 'COUPON_CODE_APPLIED',
  DOWNLOAD: 'DOWNLOAD',
  SHARE_VIDEO: 'SHARE_VIDEO',
  BACK: 'BACK',
  FEEDBACK_PROMPT_CLOSE: 'FEEDBACK_PROMPT_CLOSE',
  WHATSAPP_SUPPORT_FAB: 'WHATSAPP_SUPPORT_FAB',
  WHATSAPP_SUPPORT_SEND: 'WHATSAPP_SUPPORT_SEND',
  WHATSAPP_SUPPORT_UPGRADE: 'WHATSAPP_SUPPORT_UPGRADE',
  STREAK_REMINDERS_CTA: 'STREAK_REMINDERS_CTA',
  /** Home streak card — opens streak reminder modal. */
  STREAK_REMIND_ME_CLICKED: 'STREAK_REMIND_ME_CLICKED',
  /** Home AI analysis card — opens social analysis modal. */
  AI_SOCIAL_ANALYSIS_CONNECT_CLICKED: 'AI_SOCIAL_ANALYSIS_CONNECT_CLICKED',
  /** AI social analysis modal — submits Instagram/YouTube handles. */
  AI_SOCIAL_ANALYSIS_SUBMITTED: 'AI_SOCIAL_ANALYSIS_SUBMITTED',
};

/** Query params stored on Session Attribution (does not affect Firestore upload metadata). */
const _JOURNEY_ATTR_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'ref',
  'referral',
];

const _JOURNEY_ATTR_MAX_LEN = 256;
const _JOURNEY_REFERRER_MAX_LEN = 1024;
const _JOURNEY_PATH_MAX_LEN = 512;
/** Full landing URL (origin + path + query); hash omitted (BrowserRouter). */
const _JOURNEY_FULL_URL_MAX_LEN = 2048;

function _truncateJourneyField(value, maxLen) {
  if (value == null || typeof value !== 'string') {
    return value;
  }
  const s = value.trim();
  if (!s) {
    return undefined;
  }
  return s.length <= maxLen ? s : s.slice(0, maxLen);
}

/**
 * Snapshot current URL / referrer for journey Session Attribution (call when session id is first created).
 */
function collectJourneyAttributionFromWindow() {
  try {
    const params = new URLSearchParams(window.location.search || '');
    const metadata = {};
    for (const key of _JOURNEY_ATTR_PARAM_KEYS) {
      const raw = params.get(key);
      const v = _truncateJourneyField(raw == null ? '' : String(raw), _JOURNEY_ATTR_MAX_LEN);
      if (v) {
        metadata[key] = v;
      }
    }
    const ref = _truncateJourneyField(document.referrer || '', _JOURNEY_REFERRER_MAX_LEN);
    if (ref) {
      metadata.referrer_url = ref;
    }
    const path = window.location.pathname || '/';
    metadata.landing_path = _truncateJourneyField(path, _JOURNEY_PATH_MAX_LEN) || '/';
    const search = window.location.search || '';
    if (search) {
      metadata.landing_search = _truncateJourneyField(search, _JOURNEY_PATH_MAX_LEN);
    }
    const landingFull = _truncateJourneyField(
      `${window.location.origin}${path}${search}`,
      _JOURNEY_FULL_URL_MAX_LEN,
    );
    if (landingFull) {
      metadata.landing_url = landingFull;
    }
    return metadata;
  } catch {
    return { landing_path: '/' };
  }
}

// Generate or retrieve session ID from localStorage
const getSessionId = () => {
  let sessionId = localStorage.getItem('user_journey_session_id');
  if (!sessionId) {
    // Generate a unique session ID
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('user_journey_session_id', sessionId);
    const attribution = collectJourneyAttributionFromWindow();
    // Synchronous so this event is sent before the caller's trackEvent (e.g. home visit).
    // trackNamedEvent → trackEvent → getSessionId sees the id already set — no recursion.
    trackNamedEvent(EVENT_ENUMS.JOURNEY_SESSION_ATTRIBUTION, attribution);
  }
  return sessionId;
};

// React 18 Strict Mode (dev) runs mount effects twice; dedupe identical payloads within this window.
const _JOURNEY_DEDUPE_MS = 2500;
const _journeyRecentFingerprints = new Map();

function _buildJourneyFingerprint(sessionId, event) {
  const { timestamp: _ts, ...rest } = event;
  const keys = Object.keys(rest).sort();
  const body = keys.map((k) => `${k}:${JSON.stringify(rest[k])}`).join('|');
  return `${sessionId}|${body}`;
}

function _shouldSkipDuplicateJourneySend(fingerprint) {
  const now = Date.now();
  const last = _journeyRecentFingerprints.get(fingerprint);
  if (last !== undefined && now - last < _JOURNEY_DEDUPE_MS) {
    return true;
  }
  _journeyRecentFingerprints.set(fingerprint, now);
  if (_journeyRecentFingerprints.size > 200) {
    const cutoff = now - _JOURNEY_DEDUPE_MS * 4;
    for (const [key, t] of _journeyRecentFingerprints) {
      if (t < cutoff) {
        _journeyRecentFingerprints.delete(key);
      }
    }
  }
  return false;
}

// Fire and forget API call - doesn't wait for response
const trackEvent = (event) => {
  const sessionId = getSessionId();
  if (_shouldSkipDuplicateJourneySend(_buildJourneyFingerprint(sessionId, event))) {
    return;
  }
  const payload = {
    session_id: sessionId,
    event: event
  };

  // Use fetch with no await - fire and forget
  fetch('/api/user-journey/track', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload)
  }).catch((error) => {
    // Silently fail - we don't want to interrupt user experience
    console.debug('User journey tracking failed:', error);
  });
};

/**
 * Track a named event with optional metadata.
 * @param {string} eventName - Enum key from EVENT_ENUMS
 * @param {Object} metadata - Optional extra event fields
 */
export const trackNamedEvent = (eventName, metadata = {}) => {
  const validEvents = Object.values(EVENT_ENUMS);
  if (!validEvents.includes(eventName)) {
    console.warn(`Invalid event name: ${eventName}. Must be one of: ${validEvents.join(', ')}`);
    return;
  }

  const event = {
    type: 'event',
    name: eventName,
    timestamp: new Date().toISOString(),
    ...metadata
  };

  trackEvent(event);
};

const _JOURNEY_CC_LOGGED_PREFIX = 'journey_cc_logged_v1_';

/**
 * After /api/visitor-country returns, log only country_code once per user_journey_session_id.
 * Retries briefly if session id is not created yet (effect ordering).
 */
export function maybeTrackVisitorCountryAfterGeo(data) {
  if (!data || data.country_code == null) {
    return;
  }
  const raw = String(data.country_code).trim().toUpperCase();
  if (raw.length !== 2 || !/^[A-Z]{2}$/.test(raw)) {
    return;
  }

  const trySend = () => {
    try {
      const sessionId = localStorage.getItem('user_journey_session_id');
      if (!sessionId) {
        return false;
      }
      const flag = _JOURNEY_CC_LOGGED_PREFIX + sessionId;
      if (sessionStorage.getItem(flag)) {
        return true;
      }
      sessionStorage.setItem(flag, '1');
      trackNamedEvent(EVENT_ENUMS.VISITOR_COUNTRY, { country_code: raw });
      return true;
    } catch {
      return true;
    }
  };

  if (trySend()) {
    return;
  }
  queueMicrotask(() => {
    if (trySend()) {
      return;
    }
    setTimeout(trySend, 400);
    setTimeout(trySend, 1500);
  });
}

/**
 * Track a button click event
 * @param {string} buttonName - Enum key from BUTTON_ENUMS (e.g., BUTTON_ENUMS.UPLOAD_BUTTON)
 */
export const trackButtonClick = (buttonName) => {
  const validButtons = Object.values(BUTTON_ENUMS);
  if (!validButtons.includes(buttonName)) {
    console.warn(`Invalid button name: ${buttonName}. Must be one of: ${validButtons.join(', ')}`);
    return;
  }

  const event = {
    type: 'button',
    name: buttonName,
    timestamp: new Date().toISOString()
  };

  trackEvent(event);
};

/**
 * Track a page visit event
 */
export const trackPageVisit = () => {
  trackNamedEvent(EVENT_ENUMS.HOME_PAGE_VISIT);
};

/**
 * Track a user login event
 * @param {string} email - User's email address
 */
export const trackUserLogin = (email) => {
  if (!email) {
    console.warn('Email is required for login tracking');
    return;
  }

  trackNamedEvent(EVENT_ENUMS.LOGGED_IN, { email });
};

/**
 * Track a user logout event
 * @param {string} email - User's email address
 */
export const trackUserLogout = (email) => {
  if (!email) {
    console.warn('Email is required for logout tracking');
    return;
  }

  trackNamedEvent(EVENT_ENUMS.LOGGED_OUT, { email });
};

/**
 * Track when a video file is selected for upload
 * @param {string} filename - Name of the selected file
 */
export const trackVideoSelected = (filename) => {
  if (!filename) {
    console.warn('Filename is required for video selected tracking');
    return;
  }

  trackNamedEvent(EVENT_ENUMS.VIDEO_SELECTED_TO_UPLOAD, { filename });
};

/**
 * Track when a YouTube URL is added/changed
 * @param {string} url - YouTube URL that was added
 */
export const trackVideoUrlAdded = (url) => {
  if (!url) {
    console.warn('URL is required for video URL added tracking');
    return;
  }

  trackNamedEvent(EVENT_ENUMS.VIDEO_URL_ADDED, { urlname: url });
};

/**
 * Track when user clicks to add phone number during processing
 */
export const trackClickedAddPhoneDuringProcessing = () => {
  trackNamedEvent(EVENT_ENUMS.CLICKED_PHONE_NO_DURING_PROCESSING);
};

/**
 * Track when user provides phone number during processing
 * @param {string} phoneNumber - The phone number provided
 */
export const trackProvidedPhoneDuringProcessing = (phoneNumber) => {
  if (!phoneNumber) {
    console.warn('Phone number is required for tracking');
    return;
  }

  trackNamedEvent(EVENT_ENUMS.PROVIDED_PHONE_NO_DURING_PROCESSING, { phone_number: phoneNumber });
};

/**
 * Track when user clicks to add phone number during shorts download
 */
export const trackClickedAddPhoneDuringDownload = () => {
  trackNamedEvent(EVENT_ENUMS.CLICKED_PHONE_NO_DURING_DOWNLOAD);
};

/**
 * Track when user provides phone number during shorts download
 * @param {string} phoneNumber - The phone number provided
 */
export const trackProvidedPhoneDuringDownload = (phoneNumber) => {
  if (!phoneNumber) {
    console.warn('Phone number is required for tracking');
    return;
  }

  trackNamedEvent(EVENT_ENUMS.PROVIDED_PHONE_NO_DURING_DOWNLOAD, { phone_number: phoneNumber });
};

/**
 * Track when YouTube metadata returns video too long (exceeds duration limit)
 */
export const trackVideoTooLong = () => {
  trackNamedEvent(EVENT_ENUMS.VIDEO_TOO_LONG);
};

/**
 * Track when YouTube metadata returns video too large (exceeds size limit)
 */
export const trackVideoTooLarge = () => {
  trackNamedEvent(EVENT_ENUMS.VIDEO_TOO_LARGE);
};

export const trackSignInClick = () => trackNamedEvent(EVENT_ENUMS.SIGN_IN_CLICKED);
export const trackLoginFailed = (reason) => trackNamedEvent(EVENT_ENUMS.LOGIN_FAILED, reason ? { reason } : {});
export const trackTermsOpened = () => trackNamedEvent(EVENT_ENUMS.TERMS_OPENED);
export const trackPolicyOpened = () => trackNamedEvent(EVENT_ENUMS.POLICY_OPENED);
export const trackRefundPolicyOpened = () => trackNamedEvent(EVENT_ENUMS.REFUND_POLICY_OPENED);
export const trackPricingPageVisit = () => trackNamedEvent(EVENT_ENUMS.PRICING_PAGE_VISIT);
export const trackFeaturesPageVisit = () => trackNamedEvent(EVENT_ENUMS.FEATURES_PAGE_VISIT);
export const trackBlogPageVisit = () => trackNamedEvent(EVENT_ENUMS.BLOG_PAGE_VISIT);
export const trackAlternativesPageVisit = () => trackNamedEvent(EVENT_ENUMS.ALTERNATIVES_PAGE_VISIT);
export const trackDashboardPageVisit = () => trackNamedEvent(EVENT_ENUMS.DASHBOARD_PAGE_VISIT);
export const trackResultPageVisit = () => trackNamedEvent(EVENT_ENUMS.RESULT_PAGE_VISIT);

/** After subscription checkout; user lands on /?session=success and sees the success overlay. */
export const trackCheckoutSuccessPageVisit = (metadata = {}) =>
  trackNamedEvent(EVENT_ENUMS.CHECKOUT_SUCCESS_PAGE_VISIT, metadata);
export const trackTermsPageVisit = () => trackNamedEvent(EVENT_ENUMS.TERMS_PAGE_VISIT);
export const trackPolicyPageVisit = () => trackNamedEvent(EVENT_ENUMS.POLICY_PAGE_VISIT);
export const trackRefundPageVisit = () => trackNamedEvent(EVENT_ENUMS.REFUND_PAGE_VISIT);
export const trackPricingRegionShown = (region, priceText) =>
  trackNamedEvent(EVENT_ENUMS.PRICING_REGION_SHOWN, { region, price_text: priceText });
export const trackSubscribeNowClick = () => trackNamedEvent(EVENT_ENUMS.SUBSCRIBE_NOW_CLICKED);
export const trackCancelSubscriptionWhatsAppClick = () =>
  trackNamedEvent(EVENT_ENUMS.CANCEL_SUBSCRIPTION_WHATSAPP_CLICKED);
export const trackReferralInviteClicked = () => trackNamedEvent(EVENT_ENUMS.REFERRAL_INVITE_CLICKED);
export const trackReferralCodeCopied = () => trackNamedEvent(EVENT_ENUMS.REFERRAL_CODE_COPIED);
export const trackPaymentAbandonmentSurveyVisit = () =>
  trackNamedEvent(EVENT_ENUMS.PAYMENT_ABANDONMENT_SURVEY_VISIT);
export const trackPaymentAbandonmentSurveyClosed = () =>
  trackNamedEvent(EVENT_ENUMS.PAYMENT_ABANDONMENT_SURVEY_CLOSED);
export const trackPaymentAbandonmentSurveyContinueClicked = () =>
  trackNamedEvent(EVENT_ENUMS.PAYMENT_ABANDONMENT_SURVEY_CONTINUE_CLICKED);
export const trackPricingFaqItemOpened = (faqQuestion) => {
  const q = faqQuestion == null ? '' : String(faqQuestion).trim();
  if (!q) {
    return;
  }
  trackNamedEvent(EVENT_ENUMS.PRICING_FAQ_ITEM_OPENED, { faq_question: q });
};
export const trackCouponCodeApplied = (code) => {
  const event = {
    type: 'button',
    name: BUTTON_ENUMS.COUPON_CODE_APPLIED,
    timestamp: new Date().toISOString(),
    ...(code ? { coupon_code: code } : {})
  };
  trackEvent(event);
};
export const trackYoutubeStartDuringProcessing = () => trackNamedEvent(EVENT_ENUMS.YOUTUBE_STARTED_DURING_PROCESSING);
export const trackYoutubePauseDuringProcessing = () => trackNamedEvent(EVENT_ENUMS.YOUTUBE_PAUSED_DURING_PROCESSING);
export const trackUploadAnotherVideoClick = () => trackNamedEvent(EVENT_ENUMS.UPLOAD_ANOTHER_VIDEO_CLICKED);
export const trackClipPreviewPlayed = (filename) =>
  trackNamedEvent(EVENT_ENUMS.CLIP_PREVIEW_PLAYED, filename ? { filename } : {});

export const trackOneTapPublishClick = (metadata = {}) =>
  trackNamedEvent(EVENT_ENUMS.ONE_TAP_PUBLISH_CLICKED, metadata);
export const trackPastProjectClick = (fileStem) =>
  trackNamedEvent(EVENT_ENUMS.PAST_PROJECT_CLICKED, fileStem ? { file_stem: fileStem } : {});
export const trackPublishToSocialClick = () =>
  trackNamedEvent(EVENT_ENUMS.PUBLISH_TO_SOCIAL_CLICKED);
export const trackConnectAccountClick = (platform) =>
  trackNamedEvent(EVENT_ENUMS.CONNECT_ACCOUNT_CLICKED, platform ? { platform } : {});
export const trackOneTapScheduleClick = () =>
  trackNamedEvent(EVENT_ENUMS.ONE_TAP_SCHEDULE_CLICKED);
export const trackScheduleAClipClick = (source = 'sidebar') =>
  trackNamedEvent(EVENT_ENUMS.SCHEDULE_A_CLIP_CLICKED, source ? { source } : {});
export const trackScheduleClipDialogSubmitted = (metadata = {}) =>
  trackNamedEvent(EVENT_ENUMS.SCHEDULE_CLIP_DIALOG_SUBMITTED, metadata);
export const trackOneTapSchedulePreferencesSaved = (metadata = {}) =>
  trackNamedEvent(EVENT_ENUMS.ONE_TAP_SCHEDULE_PREFERENCES_SAVED, metadata);
export const trackDailyContentIdeaClick = () =>
  trackNamedEvent(EVENT_ENUMS.DAILY_CONTENT_IDEA_CLICKED);
export const trackDailyContentGenerateClick = (metadata = {}) =>
  trackNamedEvent(EVENT_ENUMS.DAILY_CONTENT_GENERATE_CLICKED, metadata);

// Export session ID getter for debugging if needed
export const getCurrentSessionId = getSessionId;

